import type { OrderItemStat } from '@/domain/Item';
import { api } from '@/lib/api';
import { queryOptions } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';

export type StatsFilters = {
  date_from?: string;
  date_to?: string;
};

export interface DoctorClinicBreakdown {
  clinic_id: number | null;
  clinic: string;
  users?: Array<StatUser>;
  total_users: number;
  new_users: number;
  new_revenue: number;
  accruing_revenue: number;
  period_revenue: number;
  total_revenue: number;
}

export interface StatUser {
  id: number;
  name: string;
  surname: string;
  is_new_user: boolean;
  first_time_order_amount: number;
  accruing_revenue: number;
  total_revenue: number;
}

/** New users: invited users who accepted (may include revenue from API). */
export interface InvitedUser {
  id: number;
  name: string;
  surname: string;
  email?: string;
  is_new_user?: boolean;
  first_time_order_amount?: number;
  accruing_revenue?: number;
  period_revenue?: number;
  total_revenue?: number;
}

/** Pending invitation (not yet accepted). */
export interface PendingInvitation {
  uuid: string;
  email: string;
  name: string;
  surname: string;
  role?: string | null;
  accepted_at?: string | null;
  created_at?: string;
  invitation_link?: string;
}

export interface DoctorStat {
  id?: number;
  doctor_id: number;
  name: string;
  surname: string;
  clinics: Array<DoctorClinicBreakdown>;
  users?: Array<StatUser>;
  /** New users (accepted invites) – may include revenue. */
  invited_users?: Array<InvitedUser>;
  invited_users_count: number;
  new_users: number;
  /** @deprecated use pending_invitations_count */
  invitations?: number;
  invited_users_new_revenue: number;
  invited_users_accruing_revenue: number;
  invited_users_period_revenue: number;
  invited_users_total_revenue: number;
  /** Pending invitations (not yet accepted). */
  pending_invitations?: Array<PendingInvitation>;
  pending_invitations_count?: number;
}

export interface ClinicStat {
  id?: number;
  clinic_id: number | null;
  clinic: string;
  /** New users (with revenue) – from API `users`. */
  users?: Array<StatUser>;
  invited_users?: Array<InvitedUser>;
  total_users: number;
  new_users: number;
  new_revenue: number;
  accruing_revenue: number;
  period_revenue: number;
  total_revenue: number;
  /** Pending invitation records (API sends as `pending_invitations` array). */
  pending_invitations?: Array<PendingInvitation>;
  /** Count of pending invitations (API sends as `pending_invitations_count`). */
  pending_invitations_count?: number;
}

export interface DoctorStatsSummary {
  total_patients: number;
  total_invitations: number;
  total_doctors: number;
  total_clinics: number;
}

export interface DoctorStatsResponse {
  data: Array<DoctorStat>;
  summary?: DoctorStatsSummary;
}

export interface ClinicStatsResponse {
  data: Array<ClinicStat>;
}

export interface OrderItemStatsResponse {
  data: Array<OrderItemStat>;
}

export const getDoctorStatsQuery = (filters: StatsFilters) =>
  queryOptions({
    queryKey: queryKeys.adminStats.doctors(filters),
    queryFn: async (): Promise<DoctorStatsResponse> => {
      const searchParams = new URLSearchParams();
      if (filters.date_from) searchParams.set('date_from', filters.date_from);
      if (filters.date_to) searchParams.set('date_to', filters.date_to);
      const query = searchParams.toString();
      return await api
        .get(`admin/stats/doctors${query ? `?${query}` : ''}`)
        .json<DoctorStatsResponse>();
    },
  });

export const getClinicStatsQuery = (filters: StatsFilters) =>
  queryOptions({
    queryKey: queryKeys.adminStats.clinics(filters),
    queryFn: async (): Promise<ClinicStatsResponse> => {
      const searchParams = new URLSearchParams();
      if (filters.date_from) searchParams.set('date_from', filters.date_from);
      if (filters.date_to) searchParams.set('date_to', filters.date_to);
      const query = searchParams.toString();
      return await api
        .get(`admin/stats/clinics${query ? `?${query}` : ''}`)
        .json<ClinicStatsResponse>();
    },
  });

export interface UserStatsResponse {
  total_doctors: number;
  total_patients: number;
  total_invitations: number;
  total_clinics: number;
}

interface UserStatsApiPayload {
  doctors?: number;
  total_doctors?: number;
  patients?: number;
  total_patients?: number;
  invitations?: number;
  total_invitations?: number;
  clinics?: number;
  total_clinics?: number;
}

interface UserStatsApiResponse {
  data?: UserStatsApiPayload;
}

export const getUserStatsQuery = () =>
  queryOptions({
    queryKey: queryKeys.adminStats.users(),
    queryFn: async (): Promise<UserStatsResponse> => {
      const response = await api
        .get('admin/stats/users')
        .json<UserStatsApiResponse | UserStatsApiPayload>();
      const payload: UserStatsApiPayload =
        typeof response === 'object' && 'data' in response
          ? (response.data ?? {})
          : (response as UserStatsApiPayload);

      return {
        total_doctors: payload.total_doctors ?? payload.doctors ?? 0,
        total_patients: payload.total_patients ?? payload.patients ?? 0,
        total_invitations:
          payload.total_invitations ?? payload.invitations ?? 0,
        total_clinics: payload.total_clinics ?? payload.clinics ?? 0,
      };
    },
  });

export interface OrderStatsResponse {
  not_started: number;
  in_progress: number;
  done: number;
  lead_time: number;
}

export const getOrderStatsQuery = (filters: StatsFilters) =>
  queryOptions({
    queryKey: queryKeys.adminStats.orders(filters),
    queryFn: async (): Promise<OrderStatsResponse> => {
      const searchParams = new URLSearchParams();
      if (filters.date_from) searchParams.set('date_from', filters.date_from);
      if (filters.date_to) searchParams.set('date_to', filters.date_to);
      const query = searchParams.toString();
      const res = await api
        .get(`admin/stats/orders${query ? `?${query}` : ''}`)
        .json<{ data: OrderStatsResponse }>();
      return res.data;
    },
  });

export const getOrderItemStatsQuery = (filters: StatsFilters) =>
  queryOptions({
    queryKey: queryKeys.adminStats.orderItems(filters),
    queryFn: async (): Promise<OrderItemStatsResponse> => {
      const searchParams = new URLSearchParams();
      if (filters.date_from) searchParams.set('date_from', filters.date_from);
      if (filters.date_to) searchParams.set('date_to', filters.date_to);
      const query = searchParams.toString();
      return await api
        .get(`admin/stats/order-items${query ? `?${query}` : ''}`)
        .json<OrderItemStatsResponse>();
    },
  });
